import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Fuel, 
  LogOut, 
  Globe, 
  CheckCircle, 
  XCircle, 
  MapPin, 
  Calendar, 
  Clock, 
  Bus,
  Phone,
  Navigation,
  XCircle as XCircleIcon,
  RefreshCw,
  Map,
  Circle,
  BarChart3,
  Banknote,
  Gift,
  TrendingDown
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { driverTranslations } from '@/i18n/driverTranslations';
import PushNotificationSettings from './PushNotificationSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// Trip translations
const tripTranslations = {
  lt: {
    trips: 'Reisai',
    myTrips: 'Mano reisai',
    noTrips: 'Nėra priskirtų reisų',
    startTrip: 'Pradėti reisą',
    endTrip: 'Baigti reisą',
    route: 'Maršrutas',
    start: 'Pradžia',
    end: 'Pabaiga',
    contact: 'Kontaktas',
    openInMaps: 'Atidaryti žemėlapyje',
    status: 'Būsena',
    time: 'Laikas',
    bus: 'Autobusas',
    today: 'Šiandien',
    upcoming: 'Artėjantys',
    past: 'Praeiti',
    history: 'Istorija',
    salary: 'Atlyginimas',
    totalSalary: 'Viso atlyginimas',
    period: 'Laikotarpis',
    from: 'Nuo',
    to: 'Iki',
    completedTrips: 'Baigti reisai',
    noHistory: 'Istorijos nėra',
    thisMonth: 'Šis mėnuo',
    lastMonth: 'Praėjęs mėnuo',
    allTime: 'Visas laikas',
    tripsCount: 'Reisų skaičius',
    avgConsumption: 'Vidutinės sąnaudos',
    norm: 'Norma',
    consumptionGood: '🌟 Puiku! Sąnaudos normoje!',
    consumptionWarning: '⚠️ Dėmesio! Sąnaudos nukrypsta nuo normos',
    consumptionBad: '🔥 Kritiškai! Sąnaudos viršija normą',
  },
  ru: {
    trips: 'Рейсы',
    myTrips: 'Мои рейсы',
    noTrips: 'Нет назначенных рейсов',
    startTrip: 'Начать рейс',
    endTrip: 'Завершить рейс',
    route: 'Маршрут',
    start: 'Начало',
    end: 'Конец',
    contact: 'Контакт',
    openInMaps: 'Открыть на карте',
    status: 'Статус',
    time: 'Время',
    bus: 'Автобус',
    today: 'Сегодня',
    upcoming: 'Предстоящие',
    past: 'Прошедшие',
    history: 'История',
    salary: 'Зарплата',
    totalSalary: 'Итого зарплата',
    period: 'Период',
    from: 'С',
    to: 'По',
    completedTrips: 'Завершённые рейсы',
    noHistory: 'История пуста',
    thisMonth: 'Этот месяц',
    lastMonth: 'Прошлый месяц',
    allTime: 'Всё время',
    tripsCount: 'Количество рейсов',
    avgConsumption: 'Средний расход',
    norm: 'Норма',
    consumptionGood: '🌟 Отлично! Расход в норме!',
    consumptionWarning: '⚠️ Внимание! Расход отклоняется от нормы',
    consumptionBad: '🔥 Критично! Расход за пределами нормы',
  },
  en: {
    trips: 'Trips',
    myTrips: 'My Trips',
    noTrips: 'No assigned trips',
    startTrip: 'Start Trip',
    endTrip: 'End Trip',
    route: 'Route',
    start: 'Start',
    end: 'End',
    contact: 'Contact',
    openInMaps: 'Open in Maps',
    status: 'Status',
    time: 'Time',
    bus: 'Bus',
    today: 'Today',
    upcoming: 'Upcoming',
    past: 'Past',
    history: 'History',
    salary: 'Salary',
    totalSalary: 'Total salary',
    period: 'Period',
    from: 'From',
    to: 'To',
    completedTrips: 'Completed trips',
    noHistory: 'No history',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    allTime: 'All time',
    tripsCount: 'Trips count',
    avgConsumption: 'Average consumption',
    norm: 'Norm',
    consumptionGood: '🌟 Excellent! Consumption is normal!',
    consumptionWarning: '⚠️ Warning! Consumption deviates from norm',
    consumptionBad: '🔥 Critical! Consumption exceeds norm',
  },
  pl: {
    trips: 'Kursy',
    myTrips: 'Moje kursy',
    noTrips: 'Brak przypisanych kursów',
    startTrip: 'Rozpocznij kurs',
    endTrip: 'Zakończ kurs',
    route: 'Trasa',
    start: 'Start',
    end: 'Koniec',
    contact: 'Kontakt',
    openInMaps: 'Otwórz na mapie',
    status: 'Status',
    time: 'Czas',
    bus: 'Autobus',
    today: 'Dzisiaj',
    upcoming: 'Nadchodzące',
    past: 'Minione',
    history: 'Historia',
    salary: 'Wynagrodzenie',
    totalSalary: 'Suma wynagrodzenia',
    period: 'Okres',
    from: 'Od',
    to: 'Do',
    completedTrips: 'Ukończone kursy',
    noHistory: 'Brak historii',
    thisMonth: 'Ten miesiąc',
    lastMonth: 'Poprzedni miesiąc',
    allTime: 'Cały czas',
    tripsCount: 'Liczba kursów',
    avgConsumption: 'Średnie zużycie',
    norm: 'Norma',
    consumptionGood: '🌟 Świetnie! Zużycie w normie!',
    consumptionWarning: '⚠️ Uwaga! Zużycie odbiega od normy',
    consumptionBad: '🔥 Krytycznie! Zużycie przekracza normę',
  }
};

export default function DriverRefuelingPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('fuel'); // 'fuel', 'trips', or 'history'
  const [buses, setBuses] = useState([]);
  const [selectedBusId, setSelectedBusId] = useState('');
  const [currentMileage, setCurrentMileage] = useState('');
  const [liters, setLiters] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [language, setLanguage] = useState(localStorage.getItem('driver_language') || 'lt');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Trips state
  const [trips, setTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  
  // History state
  const [historyTrips, setHistoryTrips] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [bonusesPenalties, setBonusesPenalties] = useState([]);
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`; // First day of current month
  });
  const [dateTo, setDateTo] = useState(() => {
    const date = new Date();
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const year = lastDay.getFullYear();
    const month = String(lastDay.getMonth() + 1).padStart(2, '0');
    const day = String(lastDay.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // Last day of current month
  });
  const [periodPreset, setPeriodPreset] = useState('thisMonth');
  
  const t = driverTranslations[language];
  const tt = tripTranslations[language];

  useEffect(() => {
    fetchBuses();
    fetchUserData();
  }, []);

  useEffect(() => {
    if (activeTab === 'trips') {
      fetchTrips();
    } else if (activeTab === 'history') {
      fetchHistoryTrips();
      fetchBonusesPenalties();
    }
  }, [activeTab, dateFrom, dateTo]);

  useEffect(() => {
    if (buses.length > 0 && !selectedBusId) {
      const localStorageBus = localStorage.getItem('driver_last_bus');
      if (localStorageBus) {
        const busExists = buses.find(b => b.id === localStorageBus);
        if (busExists) {
          setSelectedBusId(localStorageBus);
          return;
        }
      }
      
      if (user?.last_selected_bus_id) {
        const busExists = buses.find(b => b.id === user.last_selected_bus_id);
        if (busExists) {
          setSelectedBusId(user.last_selected_bus_id);
          return;
        }
      }
      
      if (buses[0]) {
        setSelectedBusId(buses[0].id);
      }
    }
  }, [buses]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (typeof refreshUser === 'function') {
        refreshUser();
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchBuses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/buses?thumbnails_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const regularBuses = response.data.filter(bus => bus.type !== 'personal');
      setBuses(regularBuses);
    } catch (error) {
      console.error('Error fetching buses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrips = async () => {
    setTripsLoading(true);
    try {
      const response = await axios.get(`${API}/trips`, getAuthHeaders());
      const today = new Date().toISOString().split('T')[0];
      // Filter trips for current driver - only future/today trips (not past)
      const myTrips = response.data.filter(trip => 
        trip.driver_id === user?.id && 
        trip.date >= today
      );
      setTrips(myTrips);
    } catch (error) {
      console.error('Error fetching trips:', error);
      toast.error('Ошибка загрузки рейсов');
    } finally {
      setTripsLoading(false);
    }
  };

  const fetchHistoryTrips = async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/trips`, getAuthHeaders());
      const today = new Date().toISOString().split('T')[0];
      // Filter past and today trips for current driver within date range
      const pastTrips = response.data.filter(trip => {
        if (trip.driver_id !== user?.id) return false;
        if (trip.date > today) return false; // Past + today, exclude future
        if (dateFrom && trip.date < dateFrom) return false;
        if (dateTo && trip.date > dateTo) return false;
        return true;
      });
      // Sort by date descending
      pastTrips.sort((a, b) => new Date(b.date) - new Date(a.date));
      setHistoryTrips(pastTrips);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Ошибка загрузки истории');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchBonusesPenalties = async () => {
    try {
      const response = await axios.get(`${API}/bonuses-penalties?user_id=${user?.id}`, getAuthHeaders());
      // Filter by date range
      const filtered = response.data.filter(bp => {
        const bpDate = bp.date ? bp.date.split('T')[0] : bp.created_at?.split('T')[0];
        if (dateFrom && bpDate < dateFrom) return false;
        if (dateTo && bpDate > dateTo) return false;
        return true;
      });
      setBonusesPenalties(filtered);
    } catch (error) {
      console.error('Error fetching bonuses/penalties:', error);
    }
  };

  const handlePeriodPreset = (preset) => {
    setPeriodPreset(preset);
    const now = new Date();
    
    // Helper function to format date as YYYY-MM-DD without timezone issues
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    switch (preset) {
      case 'thisMonth':
        setDateFrom(formatDate(new Date(now.getFullYear(), now.getMonth(), 1)));
        setDateTo(formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
        break;
      case 'lastMonth':
        setDateFrom(formatDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
        setDateTo(formatDate(new Date(now.getFullYear(), now.getMonth(), 0)));
        break;
      case 'allTime':
        setDateFrom('');
        setDateTo('');
        break;
      default:
        break;
    }
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('driver_language', lang);
  };

  const handleBusChange = (busId) => {
    setSelectedBusId(busId);
    localStorage.setItem('driver_last_bus', busId);
  };

  const [lastConsumption, setLastConsumption] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedBusId || !currentMileage || !liters) {
      toast.error(t.fillAllFields);
      return;
    }

    const selectedBus = buses.find(b => b.id === selectedBusId);
    if (selectedBus && parseInt(currentMileage) < selectedBus.mileage) {
      setErrorMessage(t.mileageError);
      setShowErrorModal(true);
      return;
    }

    // Calculate consumption
    const distance = selectedBus ? parseInt(currentMileage) - selectedBus.mileage : 0;
    const consumption = distance > 0 ? (parseFloat(liters) * 100 / distance).toFixed(2) : 0;
    setLastConsumption(parseFloat(consumption));

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/refuelings`,
        {
          bus_id: selectedBusId,
          current_mileage: parseInt(currentMileage),
          liters: parseFloat(liters)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowSuccessModal(true);
      setCurrentMileage('');
      setLiters('');
      
      fetchBuses();
      fetchUserData();
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || t.savingError);
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const selectedBus = buses.find(b => b.id === selectedBusId);

  // Group trips by date
  const today = new Date().toISOString().split('T')[0];
  const groupedTrips = {
    today: trips.filter(t => t.date === today).sort((a, b) => (a.time_start || '').localeCompare(b.time_start || '')),
    upcoming: trips.filter(t => t.date > today).sort((a, b) => new Date(a.date) - new Date(b.date)),
    past: trips.filter(t => t.date < today).sort((a, b) => new Date(b.date) - new Date(a.date))
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">{t.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                {activeTab === 'fuel' ? t.refueling : tt.myTrips}
              </h1>
              <p className="text-sm text-gray-600">{t.driver}: {user?.username}</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Language Selector */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <Globe className="w-4 h-4 text-gray-500 ml-1" />
                {['LT', 'RU', 'EN', 'PL'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleLanguageChange(lang.toLowerCase())}
                    className={`px-2 py-1 rounded font-semibold text-xs transition-all ${
                      language === lang.toLowerCase()
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setActiveTab('fuel')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'fuel'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Fuel className="w-4 h-4" />
              {t.refueling}
            </button>
            <button
              onClick={() => setActiveTab('trips')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'trips'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <MapPin className="w-4 h-4" />
              {tt.trips}
              {trips.length > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full ml-1">
                  {trips.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'history'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Clock className="w-4 h-4" />
              {tt.history}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-4">
        {activeTab === 'fuel' ? (
          /* Fuel Form */
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Fuel className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle>{t.enterRefuelingData}</CardTitle>
                  <CardDescription>{t.selectBusAndEnter}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label>{t.bus}</Label>
                  <Select value={selectedBusId || ''} onValueChange={handleBusChange}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder={t.selectBus} />
                    </SelectTrigger>
                    <SelectContent>
                      {buses.map((bus) => (
                        <SelectItem key={bus.id} value={bus.id}>
                          {bus.license_plate} - {bus.brand} {bus.model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mileage">{t.currentMileage}</Label>
                  <Input
                    id="mileage"
                    type="number"
                    placeholder={t.enterCurrentMileage}
                    value={currentMileage}
                    onChange={(e) => setCurrentMileage(e.target.value)}
                    className="h-12 text-lg"
                    required
                    min="0"
                  />
                  <p className="text-xs text-gray-500">
                    {t.lastRecordedMileage}: {selectedBus?.mileage?.toLocaleString() || 0} {t.km}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="liters">{t.litersRefueled}</Label>
                  <Input
                    id="liters"
                    type="number"
                    step="0.01"
                    placeholder={t.enterLiters}
                    value={liters}
                    onChange={(e) => setLiters(e.target.value)}
                    className="h-12 text-lg"
                    required
                    min="0"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 text-lg font-bold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                  disabled={submitting}
                >
                  {submitting ? `${t.save}...` : t.save}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : activeTab === 'trips' ? (
          /* Trips List */
          <div className="space-y-4">
            {/* Push Notifications Settings */}
            <PushNotificationSettings language={language} />

            {/* Refresh button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchTrips}
                disabled={tripsLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${tripsLoading ? 'animate-spin' : ''}`} />
                {language === 'ru' ? 'Обновить' : 'Refresh'}
              </Button>
            </div>

            {trips.length === 0 ? (
              <Card className="p-8">
                <div className="text-center text-gray-500">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{tt.noTrips}</p>
                </div>
              </Card>
            ) : (
              <>
                {/* Today's trips */}
                {groupedTrips.today.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {tt.today}
                    </h3>
                    <div className="space-y-3">
                      {groupedTrips.today.map(trip => (
                        <TripCard 
                          key={trip.id} 
                          trip={trip} 
                          language={language}
                          onClick={() => setSelectedTrip(trip)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming trips */}
                {groupedTrips.upcoming.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {tt.upcoming}
                    </h3>
                    <div className="space-y-3">
                      {groupedTrips.upcoming.map(trip => (
                        <TripCard 
                          key={trip.id} 
                          trip={trip} 
                          language={language}
                          onClick={() => setSelectedTrip(trip)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Past (not completed) trips */}
                {groupedTrips.past.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {tt.past}
                    </h3>
                    <div className="space-y-3">
                      {groupedTrips.past.slice(0, 5).map(trip => (
                        <TripCard 
                          key={trip.id} 
                          trip={trip} 
                          language={language}
                          onClick={() => setSelectedTrip(trip)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* History Tab */
          <div className="space-y-4">
            {/* Period Selector - Only 2 buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handlePeriodPreset('thisMonth')}
                className={`py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                  periodPreset === 'thisMonth'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {tt.thisMonth}
              </button>
              <button
                onClick={() => handlePeriodPreset('lastMonth')}
                className={`py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                  periodPreset === 'lastMonth'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {tt.lastMonth}
              </button>
            </div>

            {/* Salary Summary with Bonuses/Penalties */}
            {(historyTrips.length > 0 || bonusesPenalties.length > 0) && (() => {
              const tripsSalary = historyTrips.reduce((sum, t) => sum + (t.driver_salary || 0), 0);
              const tripsPaymentRaw = historyTrips.reduce((sum, t) => sum + (t.payment_amount || 0), 0);
              const bonusesTotal = bonusesPenalties
                .filter(bp => bp.type === 'bonus')
                .reduce((sum, bp) => sum + bp.amount, 0);
              const penaltiesTotal = bonusesPenalties
                .filter(bp => bp.type === 'penalty')
                .reduce((sum, bp) => sum + bp.amount, 0);
              // Деньги от клиента (из bonuses_penalties) - вычитаются из суммы от клиента
              const clientMoneyTotal = bonusesPenalties
                .filter(bp => bp.type === 'client_money')
                .reduce((sum, bp) => sum + bp.amount, 0);
              // От клиента = из рейсов - деньги от клиента (как в отчётах)
              const tripsPayment = tripsPaymentRaw - clientMoneyTotal;
              // Итого = ЗП - от клиента + премии - вычеты
              const total = tripsSalary - tripsPayment + bonusesTotal - penaltiesTotal;
              // Количество рабочих дней (уникальные даты рейсов)
              const workDays = new Set(historyTrips.map(t => t.date)).size;

              return (
                <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm text-green-100 uppercase">{language === 'ru' ? 'Зарплата' : 'Atlyginimas'}</p>
                        <p className="text-4xl font-bold">{tripsSalary.toFixed(0)} €</p>
                        <p className="text-sm text-green-200 mt-1">{historyTrips.length} {language === 'ru' ? 'рейсов' : 'reisų'}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-green-100 mb-1">{language === 'ru' ? 'Рабочих дней' : 'Darbo dienų'}</p>
                        <p className="text-5xl font-bold">{workDays}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <p className="text-green-100 text-xs flex items-center justify-center gap-1">
                          <BarChart3 className="w-3 h-3" /> {language === 'ru' ? 'Итого' : 'Viso'}
                        </p>
                        <p className={`font-bold ${total >= 0 ? '' : 'text-red-200'}`}>{total.toFixed(0)} €</p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <p className="text-green-100 text-xs flex items-center justify-center gap-1">
                          <Banknote className="w-3 h-3" /> {language === 'ru' ? 'От клиента' : 'Iš kliento'}
                        </p>
                        <p className="font-bold">{tripsPayment.toFixed(0)} €</p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <p className="text-green-100 text-xs flex items-center justify-center gap-1">
                          <Gift className="w-3 h-3" /> {language === 'ru' ? 'Премии' : 'Bonusai'}
                        </p>
                        <p className="font-bold">+{bonusesTotal.toFixed(0)} €</p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <p className="text-green-100 text-xs flex items-center justify-center gap-1">
                          <TrendingDown className="w-3 h-3" /> {language === 'ru' ? 'Вычеты' : 'Išskaitos'}
                        </p>
                        <p className="font-bold">-{penaltiesTotal.toFixed(0)} €</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Bonuses & Penalties List */}
            {bonusesPenalties.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-1">
                  <Gift className="w-4 h-4" /> {language === 'ru' ? 'Премии и вычеты' : 'Premijos ir išskaitos'}
                </h3>
                {bonusesPenalties.map(bp => (
                  <Card 
                    key={bp.id} 
                    className={`p-3 border-l-4 ${
                      bp.type === 'bonus' 
                        ? 'border-l-green-500 bg-green-50' 
                        : bp.type === 'client_money'
                        ? 'border-l-blue-500 bg-blue-50'
                        : 'border-l-red-500 bg-red-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 flex items-center gap-1">
                          {bp.type === 'bonus' 
                            ? <Gift className="w-4 h-4 text-green-600" /> 
                            : bp.type === 'client_money'
                            ? <Banknote className="w-4 h-4 text-blue-600" />
                            : <TrendingDown className="w-4 h-4 text-red-600" />
                          }
                          <span>
                            {bp.type === 'bonus' 
                              ? (language === 'ru' ? 'Премия' : 'Premija')
                              : bp.type === 'client_money'
                              ? (language === 'ru' ? 'Деньги от клиента' : 'Iš kliento')
                              : (language === 'ru' ? 'Вычет' : 'Išskaita')
                            }
                          </span>
                        </p>
                        <p className="text-sm text-gray-600">{bp.reason}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(bp.date || bp.created_at).toLocaleDateString('ru-RU')}
                        </p>
                      </div>
                      <p className={`text-xl font-bold ${
                        bp.type === 'bonus' ? 'text-green-600' : bp.type === 'client_money' ? 'text-blue-600' : 'text-red-600'
                      }`}>
                        {bp.type === 'bonus' ? '+' : '-'}{bp.amount.toFixed(0)} €
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* History List */}
            {historyLoading ? (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                <p>Loading...</p>
              </div>
            ) : historyTrips.length === 0 && bonusesPenalties.length === 0 ? (
              <Card className="p-8">
                <div className="text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{tt.noHistory}</p>
                </div>
              </Card>
            ) : historyTrips.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">
                  {language === 'ru' ? '🚌 Завершённые рейсы' : '🚌 Baigti reisai'}
                </h3>
                {historyTrips.map(trip => (
                  <HistoryTripCard
                    key={trip.id}
                    trip={trip}
                    language={language}
                    onClick={() => setSelectedTrip(trip)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Trip Detail Modal */}
      {selectedTrip && (
        <TripDetailModal
          trip={selectedTrip}
          language={language}
          onClose={() => setSelectedTrip(null)}
        />
      )}

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        {(() => {
          const bus = buses.find(b => b.id === selectedBusId);
          const redLow = bus?.red_threshold_low || 20;
          const yellowLow = bus?.yellow_threshold_low || 25;
          const yellowHigh = bus?.yellow_threshold_high || 30;
          const redHigh = bus?.red_threshold_high || 35;
          
          // Determine color based on thresholds
          const isGreen = lastConsumption >= yellowLow && lastConsumption <= yellowHigh;
          const isYellow = (lastConsumption > redLow && lastConsumption < yellowLow) || 
                          (lastConsumption > yellowHigh && lastConsumption < redHigh);
          const isRed = lastConsumption <= redLow || lastConsumption >= redHigh;
          
          const borderColor = isGreen ? 'border-green-500' : isYellow ? 'border-yellow-500' : 'border-red-500';
          const bgGradient = isGreen ? 'bg-gradient-to-br from-green-500 to-green-600' : 
                            isYellow ? 'bg-gradient-to-br from-yellow-500 to-yellow-600' : 
                            'bg-gradient-to-br from-red-500 to-red-600';
          const textColor = isGreen ? 'text-green-600' : isYellow ? 'text-yellow-600' : 'text-red-600';
          const emoji = isGreen ? '🌟' : isYellow ? '⚠️' : '🔥';
          const message = isGreen ? tt.consumptionGood : 
                         isYellow ? tt.consumptionWarning : 
                         tt.consumptionBad;
          
          return (
            <DialogContent className={`max-w-md mx-4 p-0 overflow-hidden border-4 ${borderColor}`}>
              <div className={`p-8 text-center ${bgGradient}`}>
                <div className="mb-4 flex justify-center">
                  <div className="bg-white rounded-full p-4 animate-bounce">
                    <span className="text-6xl">{emoji}</span>
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-white mb-3">
                  {t.refuelingSaved}
                </h2>
                <div className="bg-white/20 rounded-xl p-4 mb-4">
                  <p className="text-lg text-white/80 mb-1">{tt.avgConsumption}</p>
                  <p className="text-4xl font-bold text-white">
                    {lastConsumption ? `${lastConsumption} ${t.litersPerKm || 'л/100км'}` : '—'}
                  </p>
                  <p className="text-xs text-white/60 mt-2">
                    {tt.norm}: {yellowLow}-{yellowHigh} {t.litersPerKm || 'л/100км'}
                  </p>
                  <p className="text-sm text-white/90 mt-2">{message}</p>
                </div>
                <Button
                  onClick={() => setShowSuccessModal(false)}
                  className={`w-full h-14 text-xl font-bold bg-white shadow-lg ${textColor} hover:bg-gray-100`}
                >
                  {t.close || 'Закрыть'}
                </Button>
              </div>
            </DialogContent>
          );
        })()}
      </Dialog>

      {/* Error Modal */}
      <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <DialogContent className="max-w-md mx-4 p-0 overflow-hidden border-4 border-red-500">
          <div className="bg-gradient-to-br from-red-500 to-red-600 p-8 text-center">
            <div className="mb-4 flex justify-center">
              <div className="bg-white rounded-full p-4 animate-pulse">
                <XCircle className="w-20 h-20 text-red-500" strokeWidth={3} />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">
              {t.error || 'Ошибка'}
            </h2>
            <p className="text-xl text-white/90 mb-6">
              {errorMessage}
            </p>
            <Button
              onClick={() => setShowErrorModal(false)}
              className="w-full h-14 text-xl font-bold bg-white text-red-600 hover:bg-gray-100 shadow-lg"
            >
              {t.close || 'Закрыть'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TripCard({ trip, language, onClick }) {
  const tt = tripTranslations[language];
  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const isPast = trip.date < today;
  const isToday = trip.date === today;
  const isTomorrow = trip.date === tomorrow;

  // Format date for display
  const formatTripDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString(language === 'ru' ? 'ru-RU' : language === 'lt' ? 'lt-LT' : language === 'pl' ? 'pl-PL' : 'en-US', { month: 'short' });
    return { day, month };
  };

  const { day, month } = formatTripDate(trip.date);

  // Date label
  const getDateLabel = () => {
    if (isToday) return language === 'ru' ? 'СЕГОДНЯ' : language === 'lt' ? 'ŠIANDIEN' : language === 'pl' ? 'DZISIAJ' : 'TODAY';
    if (isTomorrow) return language === 'ru' ? 'ЗАВТРА' : language === 'lt' ? 'RYTOJ' : language === 'pl' ? 'JUTRO' : 'TOMORROW';
    return null;
  };

  // Get payment label translation
  const getPaymentLabel = () => {
    switch(language) {
      case 'lt': return 'Suma surinkti';
      case 'ru': return 'Деньги от клиента';
      case 'pl': return 'Kwota do pobrania';
      default: return 'Amount to collect';
    }
  };

  return (
    <Card 
      className={`cursor-pointer hover:shadow-lg transition-all active:scale-[0.98] ${isPast ? 'bg-gray-50 opacity-75' : 'bg-white'}`}
      onClick={onClick}
    >
      <CardContent className="p-4 sm:p-5">
        {/* Row 1: DATE and TIME */}
        <div className="flex items-center justify-between mb-3">
          {/* Date */}
          <div className={`flex items-center gap-2 ${isToday ? 'text-red-600' : 'text-gray-700'}`}>
            <Calendar className={`w-5 h-5 ${isToday ? 'text-red-500' : 'text-gray-500'}`} />
            <span className="text-lg sm:text-xl font-bold">
              {day} {month}
            </span>
            {isToday && (
              <span className="text-xs font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                {getDateLabel()}
              </span>
            )}
            {isTomorrow && (
              <span className="text-xs font-bold bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                {getDateLabel()}
              </span>
            )}
          </div>
          
          {/* Time */}
          {trip.time_start && (
            <div className="flex items-center gap-1.5">
              <Clock className={`w-5 h-5 ${isToday ? 'text-red-500' : 'text-blue-500'}`} />
              <span className={`text-lg sm:text-xl font-bold ${isToday ? 'text-red-600' : 'text-blue-600'}`}>
                {trip.time_start}
              </span>
              {trip.time_end && (
                <span className="text-base text-gray-400">— {trip.time_end}</span>
              )}
            </div>
          )}
        </div>

        {/* Bus info */}
        {trip.bus_number && (
          <div className="flex items-center justify-center gap-2 mb-3 py-2 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
            <span className="text-xl">🚌</span>
            <span className="text-lg font-bold text-purple-700">{trip.bus_number}</span>
          </div>
        )}

        {/* Row 2: ROUTE NAME */}
        <h3 className={`text-xl sm:text-2xl font-bold text-center py-2 rounded-lg ${trip.description ? 'mb-2' : 'mb-3'} ${isToday ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-800'}`}>
          {trip.route_name || (language === 'ru' ? 'Рейс' : 'Reisas')}
        </h3>

        {/* Description */}
        {trip.description && (
          <div className="mb-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-400 rounded-r-lg shadow-sm">
            <div className="flex items-start gap-2">
              <span className="text-lg">📝</span>
              <p className="text-sm sm:text-base text-gray-700 font-medium whitespace-pre-wrap leading-relaxed">
                {trip.description}
              </p>
            </div>
          </div>
        )}

        {/* Row 3: PAYMENT AMOUNT - Money from client */}
        {trip.payment_amount > 0 && (
          <div className={`flex items-center justify-between p-3 rounded-xl ${isToday ? 'bg-green-100 border-2 border-green-400' : 'bg-green-50 border border-green-300'}`}>
            <span className="text-sm sm:text-base font-semibold text-green-700">{getPaymentLabel()}</span>
            <span className="text-2xl sm:text-3xl font-black text-green-700">{trip.payment_amount} €</span>
          </div>
        )}

        {/* No payment indicator */}
        {(!trip.payment_amount || trip.payment_amount === 0) && (
          <div className="flex items-center justify-center p-2 rounded-lg bg-gray-100 text-gray-500 text-sm">
            {language === 'ru' ? 'Без оплаты' : language === 'lt' ? 'Be mokėjimo' : 'No payment'}
          </div>
        )}

        {/* Past indicator */}
        {isPast && (
          <div className="mt-3 flex items-center justify-center gap-2 text-green-600 bg-green-100 rounded-lg py-2">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">{tt.completed || 'Завершён'}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TripDetailModal({ trip, language, onClose }) {
  const tt = tripTranslations[language];
  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const isPast = trip.date < today;
  const isToday = trip.date === today;
  const isTomorrow = trip.date === tomorrow;

  // Translations for START/FINISH
  const getStartLabel = () => {
    switch(language) {
      case 'lt': return 'PRADŽIA';
      case 'ru': return 'СТАРТ';
      case 'pl': return 'START';
      default: return 'START';
    }
  };

  const getFinishLabel = () => {
    switch(language) {
      case 'lt': return 'PABAIGA';
      case 'ru': return 'ФИНИШ';
      case 'pl': return 'META';
      default: return 'FINISH';
    }
  };

  const getStopLabel = (index) => {
    switch(language) {
      case 'lt': return `STOTELĖ ${index + 1}`;
      case 'ru': return `ОСТАНОВКА ${index + 1}`;
      case 'pl': return `PRZYSTANEK ${index + 1}`;
      default: return `STOP ${index + 1}`;
    }
  };

  // Format date for display
  const formatTripDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString(language === 'ru' ? 'ru-RU' : language === 'lt' ? 'lt-LT' : 'en-US', { month: 'long' });
    const weekday = date.toLocaleDateString(language === 'ru' ? 'ru-RU' : language === 'lt' ? 'lt-LT' : 'en-US', { weekday: 'long' });
    return { day, month, weekday };
  };

  const { day, month, weekday } = formatTripDate(trip.date);

  // Date label
  const getDateLabel = () => {
    if (isToday) return language === 'ru' ? 'СЕГОДНЯ' : language === 'lt' ? 'ŠIANDIEN' : language === 'pl' ? 'DZISIAJ' : 'TODAY';
    if (isTomorrow) return language === 'ru' ? 'ЗАВТРА' : language === 'lt' ? 'RYTOJ' : language === 'pl' ? 'JUTRO' : 'TOMORROW';
    return weekday.toUpperCase();
  };

  const openInMaps = (location) => {
    if (location?.lat && location?.lng) {
      window.open(`https://www.google.com/maps?q=${location.lat},${location.lng}`, '_blank');
    } else if (location?.address) {
      window.open(`https://www.google.com/maps/search/${encodeURIComponent(location.address)}`, '_blank');
    } else if (location?.name) {
      window.open(`https://www.google.com/maps/search/${encodeURIComponent(location.name)}`, '_blank');
    } else {
      toast.error(language === 'ru' ? 'Нет данных для карты' : 'No map data');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg sm:mx-4 rounded-t-3xl sm:rounded-2xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header with Date */}
        <div className={`px-5 py-6 text-center ${isToday ? 'bg-gradient-to-r from-red-500 to-red-600' : 'bg-gradient-to-r from-indigo-600 to-purple-600'}`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
          
          {/* Date - Large */}
          <p className="text-sm font-bold tracking-wider text-white/80">
            {getDateLabel()}
          </p>
          <p className="text-6xl sm:text-7xl font-black text-white">
            {day}
          </p>
          <p className="text-xl font-medium text-white/90">
            {month}
          </p>
          
          {isPast && (
            <span className="inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full text-sm font-bold bg-white/20 text-white">
              <CheckCircle className="w-4 h-4" />
              {tt.completed || 'Завершён'}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Time - Very Large */}
          {trip.time_start && (
            <div className="text-center py-4 bg-gray-50 rounded-2xl">
              <div className="flex items-center justify-center gap-3">
                <Clock className={`w-8 h-8 ${isToday ? 'text-red-500' : 'text-blue-500'}`} />
                <span className={`text-4xl sm:text-5xl font-black ${isToday ? 'text-red-600' : 'text-blue-600'}`}>
                  {trip.time_start}
                </span>
                {trip.time_end && (
                  <>
                    <span className="text-3xl text-gray-300">—</span>
                    <span className="text-3xl sm:text-4xl font-bold text-gray-500">
                      {trip.time_end}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Route Name - Large */}
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800">
            {trip.route_name || (language === 'ru' ? 'Рейс' : 'Reisas')}
          </h2>

          {/* Description */}
          {trip.description && (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-4">
              <p className="text-lg sm:text-xl font-medium text-yellow-800 text-center">
                {trip.description}
              </p>
            </div>
          )}

          {/* Route - Start */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border-2 border-green-200">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                  <Navigation className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-green-600 tracking-wider">{getStartLabel()}</p>
                  {trip.time_start && (
                    <p className="text-xl sm:text-2xl font-bold text-green-700">{trip.time_start}</p>
                  )}
                  <p className="text-lg sm:text-xl font-semibold text-gray-800 whitespace-pre-wrap break-words">
                    {trip.start_location?.name || '—'}
                  </p>
                  {trip.start_location?.address && (
                    <p className="text-sm text-gray-500 whitespace-pre-wrap break-words">{trip.start_location.address}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => openInMaps(trip.start_location)}
                className="p-3 bg-green-500 hover:bg-green-600 rounded-xl text-white shrink-0"
              >
                <Map className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Waypoints */}
          {trip.stops && trip.stops.length > 0 && trip.stops.map((stop, index) => (
            <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border-2 border-blue-200">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                    <Circle className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-blue-600 tracking-wider">{getStopLabel(index)}</p>
                    {stop.arrival_time && (
                      <p className="text-xl sm:text-2xl font-bold text-blue-700">{stop.arrival_time}</p>
                    )}
                    <p className="text-lg sm:text-xl font-semibold text-gray-800 whitespace-pre-wrap break-words">
                      {stop.name || '—'}
                    </p>
                    {stop.address && (
                      <p className="text-sm text-gray-500 whitespace-pre-wrap break-words">{stop.address}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => openInMaps(stop)}
                  className="p-3 bg-blue-500 hover:bg-blue-600 rounded-xl text-white shrink-0"
                >
                  <Map className="w-6 h-6" />
                </button>
              </div>
            </div>
          ))}

          {/* Route - End */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-4 border-2 border-red-200">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-red-500 flex items-center justify-center shrink-0">
                  <MapPin className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-red-600 tracking-wider">{getFinishLabel()}</p>
                  {trip.time_end && (
                    <p className="text-xl sm:text-2xl font-bold text-red-700">{trip.time_end}</p>
                  )}
                  <p className="text-lg sm:text-xl font-semibold text-gray-800 whitespace-pre-wrap break-words">
                    {trip.end_location?.name || '—'}
                  </p>
                  {trip.end_location?.address && (
                    <p className="text-sm text-gray-500 whitespace-pre-wrap break-words">{trip.end_location.address}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => openInMaps(trip.end_location)}
                className="p-3 bg-red-500 hover:bg-red-600 rounded-xl text-white shrink-0"
              >
                <Map className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Bus */}
          {trip.bus_number && (
            <div className="flex items-center justify-center gap-3 bg-gray-100 rounded-xl py-3">
              <Bus className="w-6 h-6 text-gray-600" />
              <span className="text-xl font-bold text-gray-700">{trip.bus_number}</span>
            </div>
          )}

          {/* Contact - PROMINENT */}
          {(trip.contact_name || trip.contact_phone) && (
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-5 text-white">
              <p className="text-sm text-blue-100 mb-2 uppercase tracking-wider font-bold">{tt.contact}</p>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-2xl sm:text-3xl font-bold truncate">{trip.contact_name || '—'}</p>
                  {trip.contact_phone && (
                    <p className="text-xl text-blue-100">{trip.contact_phone}</p>
                  )}
                </div>
                {trip.contact_phone && (
                  <a
                    href={`tel:${trip.contact_phone}`}
                    className="p-4 sm:p-5 bg-green-500 hover:bg-green-400 rounded-2xl shadow-lg shrink-0"
                  >
                    <Phone className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Payment Amount - VERY PROMINENT */}
          {trip.payment_amount > 0 && (
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-5 text-white">
              <p className="text-sm text-green-100 mb-2 uppercase tracking-wider font-bold">
                {language === 'lt' ? 'Suma surinkti iš kliento' : 
                 language === 'ru' ? 'Сумма к оплате от клиента' : 
                 language === 'pl' ? 'Kwota do pobrania od klienta' : 
                 'Amount to collect from client'}
              </p>
              <div className="flex items-center justify-between">
                <p className="text-5xl sm:text-6xl font-black">{trip.payment_amount} €</p>
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-4xl">💰</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Close Button at Bottom */}
        <div className="px-5 py-4 border-t bg-gray-50 safe-area-inset-bottom">
          <button
            onClick={onClose}
            className="w-full py-4 bg-gray-200 hover:bg-gray-300 rounded-xl text-lg font-bold text-gray-700 transition-colors"
          >
            {language === 'ru' ? 'Закрыть' : language === 'lt' ? 'Uždaryti' : language === 'pl' ? 'Zamknij' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryTripCard({ trip, language, onClick }) {
  const tt = tripTranslations[language];
  
  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-all bg-gray-50"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-300">
                ✓
              </span>
              <span className="text-xs text-gray-500">{trip.date}</span>
            </div>
            <h3 className="font-semibold text-lg">{trip.route_name || 'Рейс'}</h3>
            
            {/* Route */}
            <div className="flex items-center gap-2 text-sm mt-1">
              <span className="text-green-600">{trip.start_location?.name || '—'}</span>
              <span className="text-gray-300">→</span>
              <span className="text-red-600">{trip.end_location?.name || '—'}</span>
            </div>
          </div>
          
          {/* Salary & Payment */}
          <div className="text-right">
            {trip.driver_salary > 0 && (
              <>
                <p className="text-xs text-gray-400">{tt.salary}</p>
                <p className="text-xl font-bold text-green-600">{trip.driver_salary} €</p>
              </>
            )}
            {trip.payment_amount > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                💵 {language === 'ru' ? 'от клиента' : 'iš kliento'}: {trip.payment_amount} €
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
